import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Photo from "@/components/Photo";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import FaqAccordion from "@/components/FaqAccordion";
import JsonLd from "@/components/JsonLd";
import Horaires from "@/components/Horaires";
import { DoctolibLogo } from "@/components/DoctolibLogo";
import useSeo from "@/hooks/useSeo";
import { SITE } from "@/constants/site";
import useI18n from "@/i18n";
import { pagePath } from "@/i18n/config";

export default function CabinetPage() {
    const { t, lang, soins, to, toSoin } = useI18n();
    const page = t.pages.cabinet;

    useSeo({
        title: t.seo.cabinet.title,
        description: t.seo.cabinet.description,
        path: pagePath(lang, "cabinet"),
        lang,
        pageKey: "cabinet",
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <JsonLd
                id="ld-faq-cabinet"
                data={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: page.faq.map(([q, a]) => ({
                        "@type": "Question",
                        name: q,
                        acceptedAnswer: { "@type": "Answer", text: a },
                    })),
                }}
            />
            <Header />
            <main className="pt-24" data-testid="page-cabinet">
                <div className="mx-auto max-w-[1080px] px-5 pb-20 pt-6">
                    <PageBreadcrumb label={t.nav.cabinet} pageKey="cabinet" />

                    <Reveal>
                        <p className="eyebrow mt-8">{page.eyebrow}</p>
                        <h1 className="title-grad mt-4 text-4xl sm:text-5xl">{page.h1}</h1>
                        <hr className="rule-gold mt-5 w-24" />
                        <p className="mt-5 max-w-[62ch] text-lg text-ink2">{page.lead}</p>
                    </Reveal>

                    <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                        {page.photos.map(([file, alt, legend, position], i) => (
                            <Reveal key={file} delay={Math.min(i * 0.06, 0.3)}>
                                <Photo
                                    file={file}
                                    alt={alt}
                                    legend={legend}
                                    position={position}
                                    ratioClass="aspect-[6/5] rounded-[18px]"
                                    eager={i === 0}
                                />
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-16 space-y-12">
                        <Reveal>
                            <section data-testid="cabinet-sur-place">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.surPlaceTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <div className="mt-4 space-y-4 text-ink2">
                                    {page.surPlace.map((p) => (
                                        <p key={p.slice(0, 40)}>{p}</p>
                                    ))}
                                </div>
                            </section>
                        </Reveal>

                        <Reveal>
                            <section data-testid="cabinet-deroule">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.derouleTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <div className="mt-4 space-y-4 text-ink2">
                                    {page.deroule.map((p) => (
                                        <p key={p.slice(0, 40)}>{p}</p>
                                    ))}
                                </div>
                            </section>
                        </Reveal>

                        <Reveal>
                            <section data-testid="cabinet-preparer">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.preparerTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <p className="mt-4 text-ink2">{page.preparerLead}</p>
                                <ul className="mt-4 space-y-2">
                                    {page.preparerItems.map((item) => (
                                        <li key={item} className="flex gap-2.5 text-ink2">
                                            <span aria-hidden="true" style={{ color: "var(--c-terra)" }}>·</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-4 text-ink2">{page.preparerFin}</p>
                                <Link
                                    to={to("conseils")}
                                    className="link-underline mt-4 inline-block text-sm font-bold text-terrat"
                                    data-testid="cabinet-link-conseils"
                                >
                                    {t.common.conseilsLink}
                                </Link>
                            </section>
                        </Reveal>

                        <Reveal>
                            <section data-testid="cabinet-soins">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.soinsTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <p className="mt-4 text-ink2">{page.soinsLead}</p>
                                <ul className="mt-4 space-y-2.5">
                                    {Object.entries(page.soinsItems).map(([key, detail]) => (
                                        <li key={key} className="flex gap-2.5 text-ink2">
                                            <span aria-hidden="true" style={{ color: "var(--c-terra)" }}>·</span>
                                            <span>
                                                <Link
                                                    to={toSoin(key)}
                                                    className="font-semibold text-terrat underline decoration-[var(--c-gold)] underline-offset-4"
                                                    data-testid={`cabinet-soin-link-${key}`}
                                                >
                                                    {soins[key].nav}
                                                </Link>
                                                {` — ${detail}.`}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-4 text-ink2">{page.soinsFin}</p>
                            </section>
                        </Reveal>

                        <Reveal>
                            <section data-testid="cabinet-cadre">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.cadreTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <div className="mt-4 space-y-4 text-ink2">
                                    {page.cadre.map((p) => (
                                        <p key={p.slice(0, 40)}>{p}</p>
                                    ))}
                                </div>
                            </section>
                        </Reveal>
                    </div>

                    <div className="mt-16 grid gap-6 md:grid-cols-2">
                        <Reveal>
                            <div className="card h-full p-7" data-testid="cabinet-adresse">
                                <h2 className="text-2xl text-terrat">{page.adresseTitle}</h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <p className="mt-4 font-semibold">{SITE.address}</p>
                                <div className="mt-5">
                                    <h3 className="text-base font-bold text-olivet">
                                        {t.common.hoursTitle}
                                    </h3>
                                    <div className="mt-2">
                                        <Horaires />
                                    </div>
                                </div>
                                <div className="mt-4 space-y-3 text-ink2">
                                    {page.adresse.map((p) => (
                                        <p key={p.slice(0, 40)}>{p}</p>
                                    ))}
                                </div>
                                <a
                                    href={SITE.mapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary mt-6"
                                    data-testid="cabinet-maps-button"
                                >
                                    <MapPin size={17} aria-hidden="true" />
                                    {t.common.viewMaps}
                                </a>
                            </div>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <div className="card h-full p-7" data-testid="cabinet-zone">
                                <h2 className="text-2xl text-terrat">{page.zoneTitle}</h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <p className="mt-4 text-ink2">{page.zoneLead}</p>
                                <ul className="mt-4 flex flex-wrap gap-2">
                                    {page.quartiers.map((q) => (
                                        <li
                                            key={q}
                                            className="rounded-full bg-white/70 px-4 py-1.5 text-sm text-ink"
                                            style={{ border: "1px solid var(--c-line)" }}
                                        >
                                            {q}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={SITE.doctolibUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary mt-6"
                                    data-testid="cabinet-rdv-button"
                                >
                                    {t.common.rdvOn}
                                    <DoctolibLogo height={15} color="#FFFFFF" />
                                </a>
                            </div>
                        </Reveal>
                    </div>

                    <div className="mt-16">
                        <Reveal>
                            <h2 className="text-2xl text-terrat md:text-3xl">{page.faqTitle}</h2>
                            <hr className="rule-gold mt-3 w-12" />
                        </Reveal>
                        <div className="mt-6">
                            <FaqAccordion items={page.faq} prefix="faq-cabinet" />
                        </div>
                        <p className="mt-8 max-w-[62ch] text-ink2">
                            {page.moreBefore}{" "}
                            <Link
                                to={to("tarifs")}
                                className="font-semibold text-terrat underline decoration-[var(--c-gold)] underline-offset-4"
                                data-testid="cabinet-link-tarifs"
                            >
                                {page.moreTarifs}
                            </Link>{" "}
                            {page.moreMiddle}{" "}
                            <Link
                                to={to("medecin")}
                                className="font-semibold text-terrat underline decoration-[var(--c-gold)] underline-offset-4"
                                data-testid="cabinet-link-medecin"
                            >
                                {page.moreMedecin}
                            </Link>{" "}
                            {page.moreAfter}
                        </p>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}
